<script lang="ts">
  // Attachment viewer (spec §10): a focus-trapped dialog with an <img> or <iframe>; Escape or a
  // click on the backdrop closes it. `store.viewerOpen` blocks the reader's auto-focus while open.
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';
  import { focusTrap } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import type { ViewerSpec } from './_local';

  let { viewer, onclose }: { viewer: ViewerSpec; onclose: () => void } = $props();

  const isImage = $derived(/^image\//i.test(viewer.type));

  $effect(() => {
    store.viewerOpen = true;
    return () => {
      store.viewerOpen = false;
    };
  });

  function onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      onclose();
    }
  }

  function onBackdrop(ev: MouseEvent): void {
    if (ev.target === ev.currentTarget) onclose();
  }
</script>

<div class="viewer" role="dialog" aria-modal="true" tabindex="-1" aria-label={viewer.name} use:focusTrap={{ initial: '.vbtn' }} onkeydown={onKey} onclick={onBackdrop}>
  <div class="viewer-bar">
    <button type="button" class="vbtn" aria-label={t('back')} onclick={onclose}><Icon name="x" size={20} /></button>
    <span class="vname" dir="ltr">{viewer.name}</span>
    <a class="vbtn" download={viewer.name} href={viewer.dlUrl} aria-label={t('download')}><Icon name="down" size={18} /></a>
  </div>
  <div class="viewer-body">
    {#if isImage}
      <img src={viewer.url} alt={viewer.name} />
    {:else}
      <iframe src={viewer.url} title={viewer.name}></iframe>
    {/if}
  </div>
</div>

<style>
  /* svelte-rwh28b (Reader) — viewer */
  .viewer {
    position: fixed;
    inset: 0;
    z-index: 60;
    background: #0b1020f0;
    display: grid;
    grid-template-rows: auto 1fr
  }

  .viewer-bar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    padding-top: calc(var(--space-3) + env(safe-area-inset-top));
    color: #fff
  }

  .vname {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--text-sm);
    text-align: center
  }

  .vbtn {
    display: grid;
    place-items: center;
    inline-size: var(--control-md);
    block-size: var(--control-md);
    border-radius: var(--radius-control);
    color: #fff;
    background: #ffffff1f
  }

  .vbtn:hover {
    background: #ffffff3d
  }

  .viewer-body {
    min-height: 0;
    display: grid;
    place-items: center;
    padding: var(--space-2);
    padding-bottom: calc(var(--space-2) + env(safe-area-inset-bottom));
    pointer-events: none
  }

  .viewer-body img,
  .viewer-body iframe {
    pointer-events: auto
  }

  .viewer-body img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: var(--radius-sm)
  }

  .viewer-body iframe {
    width: 100%;
    height: 100%;
    border: none;
    background: #fff;
    border-radius: var(--radius-sm)
  }
</style>
